/*
 * Aybak change (NEW FILE): cart request queue + "- qty +" stepper on product cards.
 * Loaded by ONE line in layout/theme.liquid (search for "aybak-cart"). See
 * ~/Desktop/my-buy-aybak-cart-backup/REAPPLY-aybak-cart.md for how to re-apply after a Cello update.
 *
 * Why: every Add to Cart used to send its own /cart/add with drawer sections (slow), and
 * overlapping requests overwrote each other on Shopify's side, so items were lost.
 *
 * What it does (only when the cart type is "Drawer" and the page has a <cart-drawer>):
 *  - Takes over product-form submits with a capture listener on window, so the theme's
 *    product-form.js and cart-drawer.js never see them. Nothing in those files is changed.
 *    If this file does not load, the theme works exactly as before (including the
 *    "1 item added" overlay in cart-drawer.js).
 *  - ONE queue for the page: one cart-changing request at a time, in click order.
 *    Waiting card adds are combined into one /cart/add.js request with an items array.
 *    Add requests do not ask for rendered sections.
 *  - Cards: spinner at once; after Shopify confirms, the button becomes "- qty +".
 *    +/- change the number at once and send one absolute /cart/change.js per product
 *    about 0.5 s after the last click. 0 removes the line.
 *  - Product page / quick add: normal button and quantity, "N items added" after confirmation.
 *  - After each confirmed change: cartUpdate is published and the drawer + header count are
 *    refreshed with one sections fetch (never two at once; the newest always wins).
 *  - The drawer is never opened by this file.
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var cfg = (script && script.dataset) || {};
  if (window.AybakCart) return;
  if (cfg.cartType !== 'drawer') return; // "Popup notification" and "Page": theme behaviour, untouched.
  if (typeof window.fetch !== 'function' || typeof window.Promise !== 'function' ||
      typeof window.MutationObserver !== 'function' || typeof window.FormData !== 'function') return;

  var DEBOUNCE_MS = 500;   // stepper: wait this long after the last +/- click
  var ADDED_MS = 1500;     // product page / quick add: "N items added" duration
  var ERROR_MS = 5000;     // error bubble duration
  var RETRY_DELAY_MS = 800;

  var CARD_SCOPE = 'product-info[data-layout="card"], .card-wrapper';
  var NOT_CARD_SCOPE = 'cart-drawer, quick-add-modal, modal-opener';

  // ---------------------------------------------------------------- helpers
  function routes() { return window.routes || {}; }
  function addUrl() { return (routes().cart_add_url || '/cart/add') + '.js'; }
  function changeUrl() { return (routes().cart_change_url || '/cart/change') + '.js'; }
  function cartUrl() { return routes().cart_url || '/cart'; }
  function drawer() { return document.querySelector('cart-drawer'); }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function str(name, fallback) { return cfg[name] || fallback; }
  function genericError() { return (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.'; }
  function itemsAddedText(n) {
    var q = window.quickOrderListStrings || {};
    var tpl = n === 1 ? (cfg.itemAdded || q.itemAdded || '[quantity] item added')
                      : (cfg.itemsAdded || q.itemsAdded || '[quantity] items added');
    return tpl.replace('[quantity]', n);
  }
  function publishSafe(evtName, data) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS[evtName], data);
    } catch (e) { /* a subscriber failing must not break the queue */ }
  }

  /** fetch that never throws on HTTP errors: resolves {status, data} or rejects {network:true}. */
  function send(url, body, isForm) {
    var headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (!isForm) headers['Content-Type'] = 'application/json';
    return window.fetch(url, { method: 'POST', headers: headers, body: isForm ? body : JSON.stringify(body), credentials: 'same-origin' })
      .then(readJson, function () { return { network: true }; });
  }
  function getJson(url) {
    return window.fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(readJson, function () { return { network: true }; });
  }
  function readJson(res) {
    return res.text().then(function (text) {
      var data = null;
      try { data = JSON.parse(text); } catch (e) { /* not JSON */ }
      var status = res.status;
      if (status < 400 && data && typeof data.status === 'number' && data.status >= 400) status = data.status;
      return { status: status, data: data };
    }, function () { return { network: true }; });
  }
  function isOk(r) { return r && !r.network && r.status >= 200 && r.status < 400 && r.data; }
  function isRetryable(r) { return !r || r.network || r.status >= 500; }
  function errorText(r) {
    var d = r && r.data;
    var msg = d && (d.description || d.message || (typeof d.errors === 'string' ? d.errors : ''));
    if (msg && typeof msg === 'object') msg = '';
    return msg ? String(msg).replace(/<[^>]*>/g, '') : genericError();
  }

  // ---------------------------------------------------------------- cart model (confirmed state only)
  var lines = [];            // [{key, variant_id, quantity, properties}]
  var itemCount = null;
  var modelVersion = 0;
  var modelTrusted = false;  // true once /cart.js has loaded at least once

  function pick(item) {
    return { key: item.key, variant_id: String(item.variant_id != null ? item.variant_id : item.id), quantity: item.quantity, properties: item.properties };
  }
  function setCart(cart) {
    lines = (cart.items || []).map(pick);
    itemCount = cart.item_count;
    modelVersion++;
    modelTrusted = true;
  }
  function upsertLine(item) {
    var l = pick(item);
    var i = lines.findIndex(function (x) { return x.key === l.key; });
    if (i === -1) { if (l.quantity > 0) lines.push(l); } else if (l.quantity > 0) lines[i] = l; else lines.splice(i, 1);
    itemCount = lines.reduce(function (s, x) { return s + x.quantity; }, 0);
    modelVersion++;
  }
  function qtyOf(vid) {
    return lines.reduce(function (s, l) { return l.variant_id === String(vid) ? s + l.quantity : s; }, 0);
  }
  function hasProps(l) { return l.properties && Object.keys(l.properties).length > 0; }
  function plainLine(vid) {
    var mine = lines.filter(function (l) { return l.variant_id === String(vid); });
    return mine.filter(function (l) { return !hasProps(l); })[0] || mine[0] || null;
  }
  function fetchCart() {
    return getJson(cartUrl() + '.js').then(function (r) {
      if (isOk(r) && Array.isArray(r.data.items)) return r.data;
      throw new Error('cart.js failed');
    });
  }

  /** Background sync with /cart.js. Ignored if the model changed while it was in flight. */
  var syncing = null;
  function syncFromServer() {
    if (syncing) return syncing;
    var ver = modelVersion;
    syncing = fetchCart().then(function (cart) {
      if (ver === modelVersion) { setCart(cart); renderAll(); }
    }).catch(function () { /* keep what we have */ }).then(function () { syncing = null; });
    return syncing;
  }

  // ---------------------------------------------------------------- per-variant UI state
  var vstate = {};
  function st(vid) {
    vid = String(vid);
    return vstate[vid] || (vstate[vid] = { pendingAdds: 0, desired: null, timer: null, limit: null });
  }
  var timersActive = 0;

  // ---------------------------------------------------------------- queue
  var queue = [];
  var inFlight = false;
  var ready = null; // initial /cart.js

  function enqueue(op) {
    queue.push(op);
    updateUnload();
    pump();
  }
  function pump() {
    if (inFlight || !queue.length) return;
    inFlight = true;
    updateUnload();
    (ready || Promise.resolve()).then(function () {
      var job;
      if (queue[0].kind === 'card-add') {
        var batch = [];
        while (queue.length && queue[0].kind === 'card-add') batch.push(queue.shift());
        job = runCardAdds(batch);
      } else {
        var op = queue.shift();
        job = op.kind === 'set' ? runSet(op) : runFormAdd(op);
      }
      return job;
    }).catch(function (e) { if (window.console) console.error('[aybak-cart]', e); })
      .then(function () { inFlight = false; updateUnload(); pump(); });
  }

  /**
   * Add items [{vid, qty}] in ONE request. Network errors / 5xx: check /cart.js first so nothing
   * is added twice, then retry the missing units once. 4xx with several items: send them one by
   * one so the good ones still land. Resolves {ok: [vid...], failed: {vid: message}, data}.
   */
  function addItems(items, allowRetry, formBody) {
    var baseline = {};
    items.forEach(function (it) { baseline[it.vid] = qtyOf(it.vid); });
    var body = formBody || { items: items.map(function (it) { return { id: Number(it.vid) || it.vid, quantity: it.qty }; }) };
    return send(addUrl(), body, !!formBody).then(function (r) {
      if (isOk(r)) {
        var added = Array.isArray(r.data.items) ? r.data.items : [r.data];
        added.forEach(upsertLine);
        return { ok: items.map(function (it) { return it.vid; }), failed: {}, data: r.data };
      }
      var retry = isRetryable(r) && allowRetry && modelTrusted;
      var split = !isRetryable(r) && items.length > 1;
      if (!retry && !split) return failAll(items, errorText(r));
      return (retry ? wait(RETRY_DELAY_MS) : Promise.resolve()).then(fetchCart).then(function (cart) {
        setCart(cart);
        var landed = [], missing = [];
        items.forEach(function (it) {
          var gap = baseline[it.vid] + it.qty - qtyOf(it.vid);
          if (gap <= 0) landed.push(it.vid); else missing.push({ vid: it.vid, qty: Math.min(gap, it.qty) });
        });
        var result = { ok: landed, failed: {}, data: cart };
        if (!missing.length) return result;
        // Retry: once, same form body when it is a single product-page add.
        var next = retry
          ? addItems(missing, false, formBody && missing[0].qty === items[0].qty ? formBody : null).then(function (r2) { return [r2]; })
          : missing.reduce(function (p, it) {
              return p.then(function (acc) { return addItems([it], false).then(function (r2) { acc.push(r2); return acc; }); });
            }, Promise.resolve([]));
        return next.then(function (results) {
          results.forEach(function (r2) {
            result.ok = result.ok.concat(r2.ok);
            Object.keys(r2.failed).forEach(function (k) { result.failed[k] = r2.failed[k]; });
            if (r2.ok.length) result.data = r2.data;
          });
          return result;
        });
      }, function () { return failAll(items, errorText(r)); });
    });
  }
  function failAll(items, message) {
    var failed = {};
    items.forEach(function (it) { failed[it.vid] = message; });
    return { ok: [], failed: failed, data: null };
  }

  function runCardAdds(batch) {
    var merged = [], index = {};
    batch.forEach(function (op) {
      if (index[op.vid] == null) { index[op.vid] = merged.length; merged.push({ vid: op.vid, qty: 0 }); }
      merged[index[op.vid]].qty += op.qty;
    });
    return addItems(merged, true).then(function (res) {
      merged.forEach(function (it) {
        var s = st(it.vid);
        s.pendingAdds = Math.max(0, s.pendingAdds - it.qty);
        if (res.failed[it.vid]) showError(it.vid, res.failed[it.vid]);
      });
      if (res.ok.length) confirmed(res.ok, res.data, 'aybak-cart');
      else { syncFromServer(); }
      merged.forEach(function (it) { renderVariant(it.vid); });
    });
  }

  function runFormAdd(op) {
    return addItems([{ vid: op.vid, qty: op.qty }], true, op.formData).then(function (res) {
      op.btn.aybakBusy = Math.max(0, (op.btn.aybakBusy || 1) - 1);
      if (!op.btn.aybakBusy) setLoading(op.btn, false);
      if (res.ok.length) {
        confirmed(res.ok, res.data, 'product-form');
        var target = op.btn;
        if (op.modal && typeof op.modal.hide === 'function') {
          target = op.modal.openedBy || null;
          try { op.modal.hide(true); } catch (e) { /* theme modal */ }
        }
        if (target) showAdded(target, op.qty);
      } else {
        var msg = res.failed[op.vid] || genericError();
        publishSafe('cartError', { source: 'product-form', productVariantId: op.vid, errors: msg, message: msg });
        if (op.pf && typeof op.pf.handleErrorMessage === 'function') { try { op.pf.handleErrorMessage(msg); } catch (e) { /* theme */ } }
        showErrorOn(op.btn, msg);
        syncFromServer();
      }
      renderVariant(op.vid);
    });
  }

  function runSet(op) {
    var vid = op.vid, desired = op.qty, s = st(vid);
    var line = plainLine(vid);
    var total = qtyOf(vid);
    var job;
    if (desired === total) job = Promise.resolve({ ok: true });
    else if (!line) job = addItems([{ vid: vid, qty: desired - total }], true).then(function (res) {
      return res.ok.length ? { ok: true, data: res.data } : { ok: false, message: res.failed[vid] };
    });
    else {
      var lineQty = Math.max(0, desired - (total - line.quantity));
      var body = { id: line.key, quantity: lineQty };
      job = send(changeUrl(), body).then(function (r) {
        if (isRetryable(r)) return wait(RETRY_DELAY_MS).then(function () { return send(changeUrl(), body); }); // absolute: safe to repeat
        return r;
      }).then(function (r) {
        if (isOk(r) && Array.isArray(r.data.items)) { setCart(r.data); return { ok: true, data: r.data }; }
        return { ok: false, message: errorText(r) };
      });
    }
    return job.then(function (res) {
      var stillPending = s.timer || queue.some(function (q) { return q.kind === 'set' && q.vid === vid; });
      if (res.ok) {
        var actual = qtyOf(vid);
        if (actual < desired) {
          s.limit = actual;
          var tpl = (window.cartStrings && window.cartStrings.quantityError) || 'You can only add [quantity] of this item to your cart.';
          showError(vid, tpl.replace('[quantity]', actual).replace(/<[^>]*>/g, ''));
        }
        if (!stillPending) s.desired = null;
        if (res.data) confirmed([vid], res.data, 'aybak-cart');
      } else {
        // Revert to the last confirmed quantity and drop anything still waiting for this product.
        if (s.timer) { clearTimeout(s.timer); s.timer = null; timersActive--; }
        queue = queue.filter(function (q) { return !(q.kind === 'set' && q.vid === vid); });
        s.desired = null;
        showError(vid, res.message || genericError());
        return syncFromServer().then(function () { renderVariant(vid); });
      }
      renderVariant(vid);
    });
  }

  /** After Shopify confirmed a change: tell the theme, refresh drawer + header count. */
  var publishingOwn = false;
  function confirmed(vids, data, source) {
    requestRefresh();
    publishingOwn = true; // the drawer's own cartUpdate re-fetch is covered by the refresh above
    try {
      publishSafe('cartUpdate', { source: source, aybak: true, productVariantId: vids.length === 1 ? vids[0] : null, productVariantIds: vids, cartData: data });
    } finally { publishingOwn = false; }
  }

  // ---------------------------------------------------------------- drawer + header refresh
  // Uses the drawer's own getSectionsToRender() and getSectionInnerHTML(), exactly like
  // CartDrawer.renderContents() in cart-drawer.js (minus its this.open()).
  // The sections come from the ROOT url ("/?sections=..."). Do NOT use "/cart?sections=..." with an
  // "Accept: application/json" header: Shopify then answers with the cart JSON instead of the sections
  // (that was the "badge stays at 0 / drawer stays empty" bug found on the test theme).
  var refreshing = false, refreshAgain = false, refreshSeq = 0;
  function sectionsUrl(ids) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + '?sections=' + ids.join(',');
  }
  function requestRefresh() {
    if (refreshing) { refreshAgain = true; return; }
    var cd = drawer();
    if (!cd || typeof cd.getSectionsToRender !== 'function' || typeof cd.getSectionInnerHTML !== 'function') return;
    refreshing = true;
    var seq = ++refreshSeq;
    var sections = cd.getSectionsToRender();
    var ids = sections.map(function (s) { return s.id; });
    var load = function () {
      return window.fetch(sectionsUrl(ids), { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          var complete = data && ids.every(function (id) { return typeof data[id] === 'string'; });
          if (!complete) throw new Error('sections response is missing ' + ids.join(', '));
          return data;
        });
    };
    load().catch(function () { return wait(RETRY_DELAY_MS).then(load); })
      .then(function (data) {
        // Serialized, so this is always the newest response; the seq check is a second guard.
        if (seq === refreshSeq) applySections(cd, sections, data);
      })
      .catch(function (e) { if (window.console) console.error('[aybak-cart] drawer/header refresh failed', e); })
      .then(function () {
        refreshing = false;
        if (refreshAgain) { refreshAgain = false; requestRefresh(); }
      });
  }
  function applySections(cd, sections, data) {
    sections.forEach(function (section) {
      // Same element lookup as CartDrawer.renderContents().
      var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!el) return;
      try {
        var oldWidth = (el.querySelector('.wbship .determinate') || {}).style;
        oldWidth = (oldWidth && oldWidth.width) || '0%';
        el.innerHTML = cd.getSectionInnerHTML(data[section.id], section.selector); // theme method, theme selector
        var bar = el.querySelector('.wbship .determinate');
        if (bar && typeof bar.animate === 'function') bar.animate([{ width: oldWidth }, { width: bar.style.width }], { duration: 1200, easing: 'ease' });
      } catch (e) { if (window.console) console.error('[aybak-cart] could not apply section ' + section.id, e); }
    });
    // Same as renderContents: re-bind the overlay that came with the new #CartDrawer content.
    var overlay = cd.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof cd.close === 'function') overlay.addEventListener('click', cd.close.bind(cd));
    // Empty / not empty: follow the freshly rendered <cart-drawer> tag (as on a fresh page load).
    try {
      var fresh = new DOMParser().parseFromString(data['cart-drawer'] || '', 'text/html').querySelector('cart-drawer');
      if (fresh) cd.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      else if (modelTrusted && itemCount != null) cd.classList.toggle('is-empty', itemCount === 0);
    } catch (e) { /* class only */ }
  }

  // ---------------------------------------------------------------- cards: find, render
  function isCardButton(btn) {
    return !!(btn && btn.closest(CARD_SCOPE) && !btn.closest(NOT_CARD_SCOPE));
  }
  function variantOf(btn) {
    var input = btn.form && btn.form.querySelector('[name="id"]');
    return input && input.value ? String(input.value) : null;
  }
  function cardButtons() {
    return Array.prototype.filter.call(document.querySelectorAll('product-form button[type="submit"][name="add"]'), isCardButton);
  }
  function productTitle(btn) {
    var ids = (btn.getAttribute('aria-labelledby') || '').split(/\s+/);
    for (var i = 0; i < ids.length; i++) {
      if (ids[i] && ids[i] !== btn.id) { var el = document.getElementById(ids[i]); if (el && el.textContent.trim()) return el.textContent.trim(); }
    }
    var card = btn.closest('.card-wrapper');
    var h = card && card.querySelector('.card__heading');
    return h ? h.textContent.trim() : '';
  }
  function cardLimit(btn, s) {
    var limit = s.limit;
    var form = btn.form;
    var q = form && (form.querySelector('[name="quantity"]') || (form.id && document.querySelector('[name="quantity"][form="' + form.id + '"]')));
    var max = q && parseInt(q.getAttribute('data-max') || q.getAttribute('max'), 10);
    if (max > 0) limit = limit == null ? max : Math.min(limit, max);
    return limit;
  }

  var renderQueued = false;
  function renderAll() {
    renderQueued = false;
    cardButtons().forEach(render);
  }
  function renderAllSoon() {
    if (renderQueued) return;
    renderQueued = true;
    Promise.resolve().then(renderAll);
  }
  function renderVariant(vid) {
    cardButtons().forEach(function (btn) { if (variantOf(btn) === String(vid)) render(btn); });
  }

  function render(btn) {
    var vid = variantOf(btn);
    if (!vid) return;
    var s = st(vid);
    var confirmedQty = qtyOf(vid);
    var showStepper = s.pendingAdds === 0 && (s.desired != null || (modelTrusted || lines.length) && confirmedQty > 0);
    setLoading(btn, s.pendingAdds > 0);
    if (showStepper) {
      var shown = s.desired != null ? s.desired : confirmedQty;
      var stepper = ensureStepper(btn);
      if (stepper.aybakVid !== vid) { stepper.aybakVid = vid; labelStepper(stepper, btn); }
      setNumber(stepper, shown);
      var limit = cardLimit(btn, s);
      stepper.querySelector('[data-aybak-step="-1"]').disabled = shown <= 0;
      stepper.querySelector('[data-aybak-step="1"]').disabled = btn.disabled || (limit != null && shown >= limit);
      if (btn.style.visibility !== 'hidden') {
        var hadFocus = document.activeElement === btn;
        btn.style.visibility = 'hidden';
        stepper.hidden = false;
        place(stepper, btn);
        // Keep keyboard users on the control they were using (no scrolling).
        if (hadFocus) focusNoScroll(stepper.querySelector('[data-aybak-step="1"]'));
      }
    } else if (btn.aybakStepper && !btn.aybakStepper.hidden) {
      var stepperFocused = btn.aybakStepper.contains(document.activeElement);
      btn.aybakStepper.hidden = true;
      btn.style.visibility = '';
      if (stepperFocused) focusNoScroll(btn);
    }
  }
  function focusNoScroll(el) {
    try { el.focus({ preventScroll: true }); } catch (e) { /* old browsers */ }
  }

  function setLoading(btn, on) {
    if (!!btn.aybakLoading === !!on) return;
    btn.aybakLoading = !!on;
    btn.classList.toggle('loading', !!on);
    var spinner = btn.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
    if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
  }

  function host(btn) {
    var h = btn.parentElement;
    if (h && !h.classList.contains('aybak-host')) h.classList.add('aybak-host');
    return h;
  }
  /** Lay an element exactly over the button's box (the button keeps its space: no layout shift). */
  function place(el, btn) {
    var h = host(btn);
    var hb = h.getBoundingClientRect(), bb = btn.getBoundingClientRect();
    el.style.left = (bb.left - hb.left - h.clientLeft) + 'px';
    el.style.top = (bb.top - hb.top - h.clientTop) + 'px';
    el.style.width = bb.width + 'px';
    el.style.height = bb.height + 'px';
  }

  var resizeObserver = typeof window.ResizeObserver === 'function' ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) { var b = e.target; if (b.aybakStepper && !b.aybakStepper.hidden) place(b.aybakStepper, b); });
  }) : null;

  function ensureStepper(btn) {
    if (btn.aybakStepper && btn.aybakStepper.isConnected) return btn.aybakStepper;
    var el = document.createElement('div');
    el.className = 'aybak-stepper';
    el.setAttribute('role', 'group');
    el.hidden = true;
    el.innerHTML =
      '<button type="button" class="aybak-stepper__btn" data-aybak-step="-1"><span aria-hidden="true">&minus;</span></button>' +
      '<span class="aybak-stepper__qty" aria-live="polite" aria-atomic="true">' +
        '<span class="visually-hidden"></span><span data-aybak-n></span></span>' +
      '<button type="button" class="aybak-stepper__btn" data-aybak-step="1"><span aria-hidden="true">+</span></button>';
    el.querySelector('.visually-hidden').textContent = str('quantityLabel', 'Quantity') + ' ';
    try {
      var cs = window.getComputedStyle(btn);
      el.style.backgroundColor = cs.backgroundColor;
      el.style.color = cs.color === 'rgba(0, 0, 0, 0)' ? '' : cs.color;
      el.style.borderRadius = cs.borderRadius;
      el.style.fontFamily = cs.fontFamily;
      el.style.fontSize = cs.fontSize;
      el.style.fontWeight = cs.fontWeight;
    } catch (e) { /* styling only */ }
    btn.insertAdjacentElement('afterend', el);
    btn.aybakStepper = el;
    el.aybakButton = btn;
    if (resizeObserver) resizeObserver.observe(btn);
    return el;
  }
  function labelStepper(el, btn) {
    var title = productTitle(btn);
    el.setAttribute('aria-label', title || str('quantityLabel', 'Quantity'));
    el.querySelector('[data-aybak-step="-1"]').setAttribute('aria-label', str('decrease', 'Decrease quantity for [product]').replace('[product]', title));
    el.querySelector('[data-aybak-step="1"]').setAttribute('aria-label', str('increase', 'Increase quantity for [product]').replace('[product]', title));
  }
  function setNumber(el, n) {
    var span = el.querySelector('[data-aybak-n]');
    if (span.textContent !== String(n)) span.textContent = String(n);
  }

  // ---------------------------------------------------------------- messages (no layout shift)
  function showError(vid, message) {
    var s = st(vid);
    var btn = s.lastBtn && s.lastBtn.isConnected && variantOf(s.lastBtn) === String(vid) ? s.lastBtn
      : cardButtons().filter(function (b) { return variantOf(b) === String(vid); })[0];
    showErrorOn(btn, message);
  }
  function showErrorOn(btn, message) {
    if (!btn || !btn.isConnected) return;
    var h = host(btn);
    var el = btn.aybakError;
    if (!el || !el.isConnected) {
      el = document.createElement('div');
      el.className = 'aybak-cart-error';
      el.setAttribute('role', 'alert');
      btn.aybakError = el;
      h.appendChild(el);
    }
    el.textContent = message;
    el.style.left = btn.offsetLeft + 'px';
    el.style.top = (btn.offsetTop + btn.offsetHeight + 4) + 'px';
    el.style.minWidth = btn.offsetWidth + 'px';
    clearTimeout(el.aybakTimer);
    el.aybakTimer = setTimeout(function () { el.remove(); }, ERROR_MS);
  }
  function clearError(btn) {
    if (btn && btn.aybakError) { clearTimeout(btn.aybakError.aybakTimer); btn.aybakError.remove(); btn.aybakError = null; }
  }

  /** "N items added" laid over the button; the button's own content is only hidden. */
  function showAdded(btn, qty) {
    if (!btn || !btn.isConnected || btn.tagName !== 'BUTTON') return;
    var text = itemsAddedText(qty);
    if (btn.aybakAdded) {
      clearTimeout(btn.aybakAdded.timer);
      btn.aybakAdded.msg.textContent = text;
    } else {
      var state = btn.aybakAdded = {
        children: Array.prototype.map.call(btn.children, function (c) { return [c, c.style.visibility]; }),
        position: btn.style.position,
        ariaLive: btn.getAttribute('aria-live'),
        msg: document.createElement('span')
      };
      state.children.forEach(function (c) { c[0].style.visibility = 'hidden'; });
      if (window.getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
      if (!state.ariaLive) btn.setAttribute('aria-live', 'polite');
      state.msg.className = 'aybak-added';
      state.msg.textContent = text;
      btn.appendChild(state.msg);
    }
    btn.aybakAdded.timer = setTimeout(function () {
      var s = btn.aybakAdded;
      s.msg.remove();
      s.children.forEach(function (c) { c[0].style.visibility = c[1]; });
      btn.style.position = s.position;
      if (!s.ariaLive) btn.removeAttribute('aria-live');
      btn.aybakAdded = null;
    }, ADDED_MS);
  }

  // ---------------------------------------------------------------- user actions
  function onSubmit(evt) {
    var form = evt.target;
    if (!form || !form.closest) return;
    var pf = form.closest('product-form');
    if (!pf || !drawer()) return;
    var btn = evt.submitter || form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]');
    if (!btn || btn.disabled) return;
    var fd = new FormData(form);
    var vid = fd.get('id') || variantOf(btn);
    if (!vid) return; // let the theme handle odd forms
    evt.preventDefault();
    evt.stopImmediatePropagation();
    vid = String(vid);
    var qty = Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
    clearError(btn);

    if (isCardButton(btn)) {
      st(vid).pendingAdds += qty;
      st(vid).lastBtn = btn;
      render(btn); // spinner now
      renderVariant(vid);
      enqueue({ kind: 'card-add', vid: vid, qty: qty });
    } else {
      if (typeof pf.handleErrorMessage === 'function') { try { pf.handleErrorMessage(); } catch (e) { /* theme */ } }
      btn.aybakBusy = (btn.aybakBusy || 0) + 1;
      setLoading(btn, true);
      enqueue({ kind: 'form-add', vid: vid, qty: qty, formData: fd, btn: btn, pf: pf, modal: pf.closest('quick-add-modal') });
    }
  }

  function onClick(evt) {
    var b = evt.target.closest && evt.target.closest('.aybak-stepper__btn');
    if (!b) return;
    evt.preventDefault();
    var stepper = b.closest('.aybak-stepper');
    var btn = stepper && stepper.aybakButton;
    if (!btn || b.disabled) return;
    var vid = variantOf(btn);
    if (!vid) return;
    clearError(btn);
    var s = st(vid);
    s.lastBtn = btn;
    var limit = cardLimit(btn, s);
    var next = (s.desired != null ? s.desired : qtyOf(vid)) + Number(b.getAttribute('data-aybak-step'));
    next = Math.max(0, limit != null ? Math.min(limit, next) : next);
    s.desired = next;
    renderVariant(vid);
    if (s.timer) clearTimeout(s.timer); else timersActive++;
    s.timer = setTimeout(function () {
      s.timer = null;
      timersActive--;
      var waiting = queue.filter(function (q) { return q.kind === 'set' && q.vid === vid; })[0];
      if (waiting) { waiting.qty = s.desired; updateUnload(); }
      else if (s.desired != null) enqueue({ kind: 'set', vid: vid, qty: s.desired });
      else updateUnload();
    }, DEBOUNCE_MS);
    updateUnload();
  }

  // ---------------------------------------------------------------- leaving the page
  var unloadOn = false;
  function onBeforeUnload(e) {
    e.preventDefault();
    e.returnValue = '';
    return '';
  }
  function updateUnload() {
    var busy = queue.length > 0 || inFlight || timersActive > 0;
    if (busy && !unloadOn) { window.addEventListener('beforeunload', onBeforeUnload); unloadOn = true; }
    else if (!busy && unloadOn) { window.removeEventListener('beforeunload', onBeforeUnload); unloadOn = false; }
  }

  // ---------------------------------------------------------------- start
  function start() {
    var cd = drawer();
    if (!cd) return; // e.g. the cart page: theme behaviour

    if (cfg.css && !document.querySelector('link[data-aybak-cart]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cfg.css;
      link.setAttribute('data-aybak-cart', '');
      document.head.appendChild(link);
    }

    window.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onClick);

    // The drawer's own "re-fetch on cartUpdate" goes through our single refresher, so two
    // drawer refreshes can never race. (Runtime only; cart.js / cart-drawer.js are unchanged.)
    if (window.customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined('cart-drawer-items').then(function () {
        var C = customElements.get('cart-drawer-items');
        if (C && C.prototype) C.prototype.onCartUpdate = function () { if (!publishingOwn) requestRefresh(); };
      });
    }

    // Changes made elsewhere (cart drawer +/-/remove, other theme code): follow the real cart.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (e) {
        if (!e || e.aybak) return;
        var c = e.cartData;
        if (c && Array.isArray(c.items) && typeof c.item_count === 'number') { setCart(c); renderAll(); }
        else syncFromServer();
      });
      if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, renderAllSoon);
    }
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id' && e.target.closest && e.target.closest('product-form')) renderAllSoon();
    });
    // Back from the cart page (bfcache) or another tab: re-read the cart once.
    window.addEventListener('pageshow', function (e) { if (e.persisted) syncFromServer(); });
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') syncFromServer(); });

    // Cards added later (infinite scroll, lazy sections, My Buy, variant re-renders).
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && !(n.className && String(n.className).indexOf('aybak-') === 0) &&
              (n.matches('button[name="add"]') || n.querySelector('button[name="add"]'))) { renderAllSoon(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    ready = fetchCart().then(function (cart) { setCart(cart); renderAll(); }, function () { /* adds still work */ });
  }

  window.AybakCart = {
    version: 1,
    _debug: function () { return { queue: queue.length, inFlight: inFlight, lines: lines.slice(), timers: timersActive }; }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
